import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
  AssetExportFormats,
  NewLayerType,
} from '../../../../../../../typings/api/skymap/rest/v1/.common';
import { SkyMapAxiosServiceFactory } from '../../../../../services/axios/skymap-axios-service-factory';
import { Design } from '../../../../../viewer/elements/design';
import { publish, SubscriptionTopic } from '../../../../../viewer/messaging/pubsub';
import { useDialog } from '../../../../hooks/use-dialog';
import { useProjectSnapshot } from '../../../../hooks/use-project-snapshot';
import { Button } from '../../../button/button';
import { Dialog } from '../../../dialog/dialog';
import { FileManagerSelectionDialog } from '../../../file-manager/dialogs/file-manager-selection-dialog/file-manager-selection-dialog';
import { InfoBox } from '../../../info-box/info-box';
import { LabelledContainer } from '../../../labelled-container/labelled-container';
import { Select } from '../../../select/select';
import { Stack } from '../../../stack/stack';

type State = 'Idle' | 'Downloading' | 'Exporting' | 'Error';

type DesignExportFormats = AssetExportFormats | '.kml' | '.shp' | '.geojson';

type ExportOptionId = 'dxf' | 'landxml' | 'kml' | 'kmlDji' | 'shape' | 'geojson';

type ExportOption = {
  name: string;
  format: DesignExportFormats;
  removeFolderTags?: boolean;
};

const exportOptions: Record<ExportOptionId, ExportOption> = {
  dxf: { name: 'DXF', format: 'dxf' },
  landxml: { name: 'LandXML', format: 'landxml' },
  kml: { name: 'KML', format: '.kml' },
  kmlDji: { name: 'KML (DJI)', format: '.kml', removeFolderTags: true },
  shape: { name: 'Shape', format: '.shp' },
  geojson: { name: 'GeoJSON', format: '.geojson' },
};

const fileFormatOptions = Object.entries(exportOptions).map(([id, option]) => ({
  id,
  name: option.name,
}));

const FileFormatInformation = (props: { optionId: ExportOptionId }) => {
  const { t } = useTranslation();

  switch (props.optionId) {
    case 'dxf':
      return (
        <span>
          <Trans
            components={{ bold: <strong /> }}
            i18nKey="design.exportDialog.infoDxf"
            ns="skyviewAssetMenu"
          />
        </span>
      );
    case 'landxml':
      return <span>{t('design.exportDialog.infoLandXml', { ns: 'skyviewAssetMenu' })}</span>;
    case 'kml':
      return <span>{t('design.exportDialog.infoKml', { ns: 'skyviewAssetMenu' })}</span>;
    case 'kmlDji':
      return <span>{t('design.exportDialog.infoKmlDji', { ns: 'skyviewAssetMenu' })}</span>;
    case 'shape':
      return <span>{t('design.exportDialog.infoShape', { ns: 'skyviewAssetMenu' })}</span>;
    case 'geojson':
      return <span>{t('design.exportDialog.infoGeojson', { ns: 'skyviewAssetMenu' })}</span>;
    default:
      return null;
  }
};

interface Props {
  asset: Design;
  onClose: () => void;
  onDirectDownload: (options: {
    format: DesignExportFormats;
    saveToFileManager: boolean;
    newLayerType?: NewLayerType;
    removeFolderTags?: boolean;
    folderId?: string;
  }) => Promise<void>;
}

const ExportAssetDialog = (props: Props) => {
  const { projectId } = useProjectSnapshot();
  const { t } = useTranslation();
  const [state, setState] = React.useState<State>('Idle');
  const fileManagerSelectionDialog = useDialog();
  const loading = state === 'Downloading' || state === 'Exporting';
  const [optionId, setOptionId] = useState<ExportOptionId>('dxf');
  const [exportType, setExportType] = useState<NewLayerType>('POLYGON');
  const { name, format, removeFolderTags } = exportOptions[optionId];

  const shouldUseCustomNewLayerType = format === '.shp';
  const exportTypeItems = useMemo<{ name: string; id: NewLayerType }[]>(
    () => [
      {
        name: t('design.exportDialog.exportTypes.polygons', { ns: 'skyviewAssetMenu' }),
        id: 'POLYGON',
      },
      {
        name: t('design.exportDialog.exportTypes.lines', { ns: 'skyviewAssetMenu' }),
        id: 'MULTILINESTRING',
      },
      {
        name: t('design.exportDialog.exportTypes.points', { ns: 'skyviewAssetMenu' }),
        id: 'POINTS',
      },
    ],
    [t],
  );

  const directDownload = async () => {
    try {
      setState('Downloading');
      await props.onDirectDownload({
        format,
        saveToFileManager: false,
        newLayerType: shouldUseCustomNewLayerType ? exportType : undefined,
        removeFolderTags,
      });
      props.onClose();
    } catch {
      setState('Error');
    }
  };

  const exportToFileManager = async (currentFolderId?: string) => {
    try {
      setState('Exporting');
      fileManagerSelectionDialog.hide();

      if (format === '.kml' || format === '.shp' || format === '.geojson') {
        props.onClose();
        await props.onDirectDownload({
          format,
          newLayerType: shouldUseCustomNewLayerType ? exportType : undefined,
          removeFolderTags,
          saveToFileManager: true,
          folderId: currentFolderId,
        });
        return;
      } else {
        const result = await SkyMapAxiosServiceFactory.instance.createAssetServiceV1().getExport({
          path: { dynamicAssetId: props.asset.assetId ?? '' },
          query: { format },
        });

        const body = {
          uploadId: result.data.uploadId,
          description: 'Design export',
        };

        if (currentFolderId) {
          await SkyMapAxiosServiceFactory.instance.createFolderServiceV1().createSubFolderFile({
            path: { folderId: currentFolderId },
            body,
          });
        } else {
          await SkyMapAxiosServiceFactory.instance.createProjectServiceV1().createRootFolderFile({
            path: { projectId: projectId ?? '' },
            body,
          });
        }
      }

      publish(SubscriptionTopic.ToastrMessage, {
        type: 'success',
        message: t('design.exportDialog.exportSuccessful', { ns: 'skyviewAssetMenu' }),
      });

      props.onClose();
    } catch {
      setState('Error');
    }
  };

  return (
    <>
      <Dialog
        closeIcon={false}
        closeOnDimmerClick={false}
        maxHeight={false}
        width={600}
        onClose={props.onClose}
      >
        {{
          header: t('design.exportDialog.title', { ns: 'skyviewAssetMenu' }),
          content: (
            <Stack spacing={0.5}>
              <InfoBox bottomMargin={true} color="yellow">
                <Stack spacing={1}>
                  <span>{t('design.exportDialog.info', { ns: 'skyviewAssetMenu' })}</span>
                  <LabelledContainer text={name}>
                    <FileFormatInformation optionId={optionId} />
                  </LabelledContainer>
                </Stack>
              </InfoBox>

              {shouldUseCustomNewLayerType && (
                <LabelledContainer text={t('exportType', { ns: 'common' })}>
                  <Select
                    options={exportTypeItems}
                    value={exportType}
                    onChange={(e) => setExportType(e.target.value as NewLayerType)}
                  />
                </LabelledContainer>
              )}

              <LabelledContainer text={t('fileFormat', { ns: 'common' })}>
                <Select
                  options={fileFormatOptions}
                  value={optionId}
                  onChange={(e) => setOptionId(e.target.value as ExportOptionId)}
                />
              </LabelledContainer>

              {state === 'Error' && (
                <InfoBox color="red">{t('unexpectedError', { ns: 'common' })}</InfoBox>
              )}
            </Stack>
          ),
          footer: {
            right: (
              <>
                <Button
                  color="secondary"
                  disabled={loading}
                  leftIcon={{ icon: ['fad', 'download'] }}
                  loading={state === 'Downloading'}
                  variant="contained"
                  onClick={() => void directDownload()}
                >
                  {t('download', { ns: 'common' })}
                </Button>
                <Button
                  color="primary"
                  disabled={loading}
                  loading={state === 'Exporting'}
                  variant="contained"
                  onClick={() => fileManagerSelectionDialog.show()}
                >
                  {t('design.exportDialog.export', { ns: 'skyviewAssetMenu' })}
                </Button>
                <Button disabled={loading} variant="text" onClick={props.onClose}>
                  {t('cancel', { ns: 'common' })}
                </Button>
              </>
            ),
          },
        }}
      </Dialog>

      {fileManagerSelectionDialog.render(
        <FileManagerSelectionDialog
          selectionMode="SingleFolder"
          onCancel={() => fileManagerSelectionDialog.hide()}
          onSelect={(_, __, folderId) => exportToFileManager(folderId)}
        />,
      )}
    </>
  );
};

export { ExportAssetDialog, Props as ExportAssetDialogProps };
